/**
 * TarihGirdisi.jsx — gün/ay/yıl tarih alanı (backlog #560).
 *
 * Yerel `<input type="date">` tarihi tarayıcının / işletim sisteminin dilinde gösterir (İngilizce Chrome'da aa/gg/yyyy) ve hiçbir
 * öznitelik bunu değiştiremez. Burada gösterilen ve yazılan biçim HER ZAMAN gg/aa/yyyy; dışarı verilen değer yine yyyy-aa-gg, yani
 * API sözleşmesi ve çağıranların karşılaştırmaları (`tarih < bugunYmd()`) değişmez.
 *
 * `onChange(ymd)` — yerel olay değil, doğrudan "yyyy-aa-gg" metni gelir; boş ya da geçersiz yazı "" döner. Yazılan metin sıkı çözülür
 * (31/02/2026 → boş) ve 5.11.2026 / 05-11-2026 / 05112026 da kabul edilir. `min` / `max` yalnızca takvimde dışarıda kalan günleri
 * soluklaştırır; yazılan tarihi silmez (çağıranın `invalid` mesajı gösterebilsin diye, yerel `min` / `max` davranışı gibi).
 *
 * Yazarken (#733): görünür alan klavyeyle doldurulabilir — yalnızca rakamlar alınır, 03062027 yazarken 03/06/2027 görünür (eğik çizgiler
 * kendiliğinden gelir, geri silme çalışır) ve 8. rakam girilir girilmez tarih atanır (blur / Enter beklenmez). Olmayan gün (31/02/2027)
 * kırmızı + "Geçersiz tarih" ile reddedilir. Takvim isteyen için yine açılır; tam tarih yazılınca kapanır.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";
import { Input } from "reactstrap";
import { useTranslation } from "react-i18next";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Gün/ay/yıl sayıları gerçek bir takvim günü mü? (31 Şubat'ı bir sonraki aya taşımaz.) */
function gunOlustur(y, a, g) {
  const d = new Date(y, a - 1, g);
  return d.getFullYear() === y && d.getMonth() === a - 1 && d.getDate() === g ? d : undefined;
}

/** flatpickr'ın gevşek çözümleyicisi yerine: "d/m/Y" (yazılan) ve "Y-m-d" (değer) biçimlerini sıkı çözer; olmazsa undefined. */
export function tarihCoz(metin, bicim) {
  const s = String(metin ?? "").trim();
  if (bicim === "Y-m-d") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    return m ? gunOlustur(+m[1], +m[2], +m[3]) : undefined;
  }
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(s) || /^(\d{2})(\d{2})(\d{4})$/.exec(s);
  return m ? gunOlustur(+m[3], +m[2], +m[1]) : undefined;
}

/** Rakamları gg/aa/yyyy maskesine döker: "0306" → "03/06", "03062027" → "03/06/2027". En çok 8 rakam; eğik çizgi ancak sonraki rakam yazılınca gelir (geri silme takılmasın). */
export function tarihMaskele(ham) {
  const r = String(ham ?? "").replace(/\D/g, "").slice(0, 8);
  return [r.slice(0, 2), r.slice(2, 4), r.slice(4)].filter(Boolean).join("/");
}

const rakamlar = (s) => s.replace(/\D/g, "");

// `min` / `max`: yalnızca takvimde dışarıda kalan günleri soluklaştırır. `bsSize={null}` normal boy; `inputStyle` görünür kutuya biner.
// `yerTutucuMetni`: boş kutuda "gg/aa/yyyy" yerine gösterilecek soluk metin (#718: rolün boş bırakılan kurulumu fuarın kurulum tarihini gösterir).
export default function TarihGirdisi({ value = "", onChange, min, max, invalid = false, disabled = false, bsSize = "sm", id, className, style, inputStyle, yerTutucuMetni }) {
  const { t, i18n } = useTranslation("common");
  const yerTutucu = yerTutucuMetni || t("tarihYerTutucu");
  const fpRef = useRef(null);
  const [yazimHatasi, setYazimHatasi] = useState(false); // 8 rakam yazıldı ama olmayan bir gün (31/02/2027)
  const tr = (i18n?.language || "tr").startsWith("tr");

  // `onChange` <Flatpickr> prop'u olarak verilirse react-flatpickr her render'da kancayı yeniden kurar (`set` → updateValue) ve yazılmakta olan
  // metni siler; bu yüzden kararlı bir seçenek olarak verilir, çağıranın son `onChange`'ini ref'ten okur.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const secenekler = useMemo(() => ({
    onChange: (_dates, ymdMetin) => onChangeRef.current?.(ymdMetin || ""),
    altInput: true,
    altFormat: "d/m/Y",
    dateFormat: "Y-m-d",
    allowInput: true,
    disableMobile: true,
    locale: tr ? Turkish : { firstDayOfWeek: 1 },
    parseDate: tarihCoz,
    errorHandler: () => {},
    onDayCreate: (_dates, _str, _fp, gun) => {
      const g = ymd(gun.dateObj);
      if ((min && g < min) || (max && g > max)) gun.classList.add("flatpickr-disabled");
    },
  }), [tr, min, max]);

  // flatpickr'ın oluşturduğu görünür alan (altInput) React'ın bilmediği bir düğüm: sınıf, devre dışı, kimlik ve yer tutucu buradan eşlenir.
  useEffect(() => {
    const alan = fpRef.current?.altInput;
    if (!alan) return;
    alan.classList.toggle("is-invalid", !!invalid || yazimHatasi);
    alan.disabled = !!disabled;
    alan.placeholder = yerTutucu;
    alan.inputMode = "numeric"; // telefonda rakam tuş takımı; ayraçları maske koyar
    if (id) alan.id = id;
    if (inputStyle) Object.assign(alan.style, inputStyle);
  }, [invalid, yazimHatasi, disabled, yerTutucu, id, inputStyle]);

  // #733: yazarken maske + 8. rakamda atama. Görünür alan flatpickr'ın düğümü olduğundan dinleyici elle bağlanır.
  useEffect(() => {
    const fp = fpRef.current;
    const alan = fp?.altInput;
    if (!alan || disabled) return undefined;
    const imleciKoy = (konum) => { try { alan.setSelectionRange(konum, konum); } catch { /* alan odakta değil */ } };
    const girdi = (e) => {
      const ham = alan.value;
      const imlec = alan.selectionStart ?? ham.length;
      let once = rakamlar(ham.slice(0, imlec)); // imleçten önceki rakamlar
      const sonra = rakamlar(ham.slice(imlec));
      const yapisan = /^\s*(\d{1,2})[./-](\d{1,2})[./-](\d{4})\s*$/.exec(ham);
      let ekle = sonra;
      if ((/^insertFrom/.test(e.inputType || "") || (e.data || "").length > 1) && yapisan) { // 3.6.2027 yapıştırıldı ya da tek seferde eklendi (otomatik doldurma)
        once = yapisan[1].padStart(2, "0") + yapisan[2].padStart(2, "0") + yapisan[3];
        ekle = "";
      } else if (e.inputType === "insertText" && /^[./-]$/.test(e.data || "") && (once.length === 1 || once.length === 3) && !once.endsWith("0")) {
        once = `${once.slice(0, -1)}0${once.slice(-1)}`; // 3. → 03/
      }
      const hepsi = (once + ekle).slice(0, 8);
      const yeni = tarihMaskele(hepsi);
      const sira = Math.min(once.length, hepsi.length);
      const konum = Math.min(sira + (sira > 2 ? 1 : 0) + (sira > 4 ? 1 : 0), yeni.length);
      if (alan.value !== yeni) alan.value = yeni;
      imleciKoy(konum);
      if (hepsi.length < 8) { setYazimHatasi(false); return; }
      const d = tarihCoz(yeni, "d/m/Y");
      setYazimHatasi(!d);
      if (!d) return;
      const mevcut = fp.selectedDates[0];
      if (!mevcut || ymd(mevcut) !== ymd(d)) fp.setDate(d, true);
      fp.close();
      imleciKoy(konum);
    };
    const cikti = () => setYazimHatasi(false); // flatpickr geçersiz yazıyı blur'da zaten siler
    alan.addEventListener("input", girdi);
    alan.addEventListener("blur", cikti);
    return () => { alan.removeEventListener("input", girdi); alan.removeEventListener("blur", cikti); };
  }, [disabled]);

  return (
    <div className={className} style={style}>
      <Flatpickr
        value={value || ""}
        options={secenekler}
        onCreate={(fp) => { fpRef.current = fp; }}
        render={(_props, ref) => <Input innerRef={ref} bsSize={bsSize} autoComplete="off" disabled={disabled} placeholder={yerTutucu} />}
      />
      {yazimHatasi && <div className="text-danger" style={{ fontSize: 11, lineHeight: 1.3, marginTop: 2 }}>{t("tarihGecersiz")}</div>}
    </div>
  );
}
